import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authService } from '../services/authService';
import BrandLogo from './BrandLogo';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkInvalid = !email || !token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.resetPassword(email, token, newPassword);
      if (result.success) {
        // resetPassword logs the user in on success
        navigate('/', { replace: true });
      } else {
        setError(result.error || 'Password reset failed. Please request a new link.');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" sx={{ padding: 2 }}>
      <Paper
        elevation={6}
        sx={{
          padding: { xs: 3, sm: 4 },
          maxWidth: 450,
          width: '100%',
          borderRadius: 4,
          border: '1px solid rgba(17, 191, 174, 0.18)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,252,255,0.94) 100%)',
          boxShadow: '0 24px 60px rgba(5, 27, 42, 0.22)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <BrandLogo size={70} stacked textColor="#08263A" accentColor="#0EAFA0" />
        </Box>

        <Typography variant="h6" align="center" sx={{ fontWeight: 700, mb: 1 }}>
          Set a new password
        </Typography>

        {linkInvalid ? (
          <>
            <Alert severity="error" sx={{ mt: 1 }}>
              This reset link is invalid or incomplete. Please request a new one from the login page.
            </Alert>
            <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/')}>
              Back to Login
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 1 }}>
              Resetting the password for <strong>{email}</strong>
            </Typography>

            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              name="newPassword"
              autoComplete="new-password"
              autoFocus
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              helperText="Minimum 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm New Password"
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              autoComplete="new-password"
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #08324A 0%, #0C5F7A 56%, #10AFA5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0A3D5A 0%, #0E6D88 56%, #12B9A8 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPassword;
